'use client'

import { useCallback, useMemo, useState } from 'react'
import type { PaymentMethod } from '@/root/shared-types/reservation/reservation-types'
import { getEnabledPaymentMethodsInDisplayOrder } from '@/root/shared-helpers/payment/payment-helpers'

interface UsePaymentSelectionProps {
  initialPaymentMethods: PaymentMethod[]
  amount: number
  creditBalance: number
  isAgree: boolean
  isError: boolean
  loading: boolean
}

/** The payment methods the backend offers (enabled, by `seqN`), the chosen one and whether the pay button is blocked. */
export const usePaymentSelection = ({
  initialPaymentMethods,
  amount,
  creditBalance,
  isAgree,
  isError,
  loading,
}: UsePaymentSelectionProps) => {
  const [allPaymentMethods, setAllPaymentMethods] = useState(initialPaymentMethods)

  const isFreeTicket = allPaymentMethods.length === 1 && allPaymentMethods[0].code === 'NONE'

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod['code'] | undefined>(
    isFreeTicket ? allPaymentMethods[0]?.code : undefined
  )

  const paymentMethods = useMemo(() => getEnabledPaymentMethodsInDisplayOrder(allPaymentMethods), [allPaymentMethods])

  const onSelect = (selected: PaymentMethod) => {
    setSelectedMethod(selected.code)
  }

  /** Puts a refetched list in place; the selection is dropped only when the new list no longer offers it. */
  const replacePaymentMethods = useCallback((methods: PaymentMethod[]) => {
    setAllPaymentMethods(methods)
    setSelectedMethod((current) =>
      getEnabledPaymentMethodsInDisplayOrder(methods).some((method) => method.code === current) ? current : undefined
    )
  }, [])

  const hasCreditMethod = allPaymentMethods.some((method) => method.code === 'CREDIT')

  const isCreditInsufficient = creditBalance < amount

  const isNextDisabled =
    !isAgree || isError || !selectedMethod || loading || (selectedMethod === 'CREDIT' && isCreditInsufficient)

  return {
    isFreeTicket,
    paymentMethods,
    hasCreditMethod,
    replacePaymentMethods,
    selectedMethod,
    onSelect,
    creditBalance,
    isCreditInsufficient,
    isNextDisabled,
  }
}
